import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { INTEGRATION_CONFIGS } from 'components/IntegrationsBanner/integrationConfig'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { ScheduledPostTarget } from 'types'
import { getPlatformDisplayName } from 'utils/platformDisplayNames'
import { PLATFORM_CHARACTER_LIMITS, type PlatformLimits } from 'components/ValidationBanner/utils'
import { getFriendlyErrorMessage } from '../Toolbar/postNotificationBannerConfig'
import { hasContentWarning } from './channelWarnings'

export type PreviewAlertAction = 'refresh-connection' | 'adjust-caption'

export interface PreviewAlertArgs {
  almanacFile: AlmanacFileShowItem
  disconnectedIntegrations: ExternalIntegration[]
}

export interface PreviewAlert {
  variant: 'error' | 'warning'
  message: string
  actionLabel?: string
  action?: PreviewAlertAction
  target: ScheduledPostTarget | null
  testId: string
}

const NO_MATCH = Symbol('no-match')
type RenderResult = ScheduledPostTarget | null | typeof NO_MATCH

export interface PreviewAlertConfig {
  testId: string
  isRendered: (args: PreviewAlertArgs) => RenderResult
  build: (args: PreviewAlertArgs, target: ScheduledPostTarget | null) => PreviewAlert
}

const getTargets = (almanacFile: AlmanacFileShowItem): ScheduledPostTarget[] => almanacFile.scheduled_post_targets ?? []

const PROVIDER_LIST_FORMAT = new Intl.ListFormat('en', { style: 'long', type: 'conjunction' })
const POST_LEVEL_ERROR_CHANNELS: ReadonlyArray<ChannelType> = [ChannelType.Newsletter, ChannelType.BlogPost]

// Mirrors V2's getProviderName (reads from INTEGRATION_CONFIGS) so V3 banner
// wording matches the legacy PostNotificationBanner character-for-character.
const PROVIDER_TO_DISPLAY_NAME = new Map(Object.values(INTEGRATION_CONFIGS).map((c) => [c.provider, c.displayName]))

const getBannerProviderName = (provider: ExternalIntegrationEnum): string =>
  PROVIDER_TO_DISPLAY_NAME.get(provider) ?? getPlatformDisplayName(provider)

export const previewAlertBannerConfigs: PreviewAlertConfig[] = [
  {
    testId: 'connection-expired',
    isRendered: ({ almanacFile, disconnectedIntegrations }) => {
      if (!disconnectedIntegrations.length) return NO_MATCH
      const targets = getTargets(almanacFile)

      const allPublished = targets.length > 0 && targets.every((t) => t.status === 'published')
      if (allPublished) return NO_MATCH

      const disconnectedIds = new Set(disconnectedIntegrations.map((i) => i.id))

      const mainIntegration = almanacFile.scheduled_post?.external_integration
      if (mainIntegration && disconnectedIds.has(mainIntegration.id)) {
        const matching = targets.find((t) => t.external_integration_id === mainIntegration.id)
        if (matching) return matching
        if (targets[0]) return targets[0]
      }

      return targets.find((t) => t.status === 'failed' && disconnectedIds.has(t.external_integration_id)) ?? NO_MATCH
    },
    build: ({ almanacFile, disconnectedIntegrations }, target) => {
      const disconnectedIds = new Set(disconnectedIntegrations.map((i) => i.id))
      const targets = getTargets(almanacFile)

      const providers = new Set<string>()
      for (const t of targets) {
        if (t.status === 'failed' && disconnectedIds.has(t.external_integration_id)) {
          providers.add(getBannerProviderName(t.provider))
        }
      }
      const mainIntegration = almanacFile.scheduled_post?.external_integration
      if (mainIntegration && disconnectedIds.has(mainIntegration.id)) {
        providers.add(getBannerProviderName(mainIntegration.provider))
      }

      const providerList = Array.from(providers)
      const message =
        providerList.length === 1
          ? `Your ${providerList[0]} connection expired.`
          : `Your ${PROVIDER_LIST_FORMAT.format(providerList)} connections have expired.`

      return {
        variant: 'error',
        message,
        actionLabel: 'Refresh Connection',
        action: 'refresh-connection',
        target,
        testId: 'connection-expired',
      }
    },
  },
  {
    testId: 'failed-target',
    isRendered: ({ almanacFile }) => {
      const targets = getTargets(almanacFile)
      return targets.find((t) => t.status === 'failed') ?? NO_MATCH
    },
    build: ({ almanacFile }, target) => {
      const failed = getTargets(almanacFile).filter((t) => t.status === 'failed')
      const withErrors = failed.filter((t) => !!t.error_message)

      let message: string
      if (withErrors.length > 0) {
        message = withErrors
          .map((t) => {
            const platform = getBannerProviderName(t.provider)
            const friendly = t.error_message ? getFriendlyErrorMessage(t.error_message, t.provider) : null
            const text = friendly?.message ?? t.error_message ?? ''
            return `${platform} - ${text}`
          })
          .join('\n')
      } else if (failed.length === 1) {
        message = 'A platform failed to post. Please check the details below.'
      } else {
        message = `${failed.length} platforms failed to post. Please check the details below.`
      }

      return {
        variant: 'error',
        message,
        actionLabel: 'Refresh Connection',
        action: 'refresh-connection',
        target,
        testId: 'failed-target',
      }
    },
  },
  {
    testId: 'post-level-error',
    isRendered: ({ almanacFile }) => {
      const post = almanacFile.scheduled_post
      if (!post) return NO_MATCH
      if (!post.source_channel || !POST_LEVEL_ERROR_CHANNELS.includes(post.source_channel as ChannelType)) {
        return NO_MATCH
      }

      const targets = getTargets(almanacFile)
      if (targets.some((t) => t.status === 'failed')) return NO_MATCH

      if (!post.posting_failed && !post.error_message) return NO_MATCH

      // Post-level errors are not tied to a target; build ignores this.
      return null
    },
    build: ({ almanacFile }) => {
      const post = almanacFile.scheduled_post
      const rawError = post?.error_message ?? ''
      let message: string
      if (rawError) {
        const friendly = getFriendlyErrorMessage(rawError, undefined)
        message = friendly?.message ?? rawError
      } else {
        message = 'This post failed to publish. Please check the details and try again.'
      }

      return {
        variant: 'error',
        message,
        target: null,
        testId: 'post-level-error',
      }
    },
  },
  {
    testId: 'content-warning',
    isRendered: ({ almanacFile }) => {
      if (almanacFile.post_status !== PostStatusPropertyValue.AutoPostScheduled) return NO_MATCH
      const targets = getTargets(almanacFile)
      return targets.find((t) => hasContentWarning(t)) ?? NO_MATCH
    },
    build: (_args, target) => {
      const provider = target?.provider
      const platform = provider ? getPlatformDisplayName(provider) : ''
      const key = provider as PlatformLimits
      const limit = provider ? PLATFORM_CHARACTER_LIMITS[key]?.limit : undefined
      return {
        variant: 'warning',
        message: `Caption is too long for ${platform} — trim it down to ${limit} characters.`,
        actionLabel: 'Adjust Caption',
        action: 'adjust-caption',
        target,
        testId: 'content-warning',
      }
    },
  },
]

export const derivePreviewAlert = (args: PreviewAlertArgs): PreviewAlert | null => {
  for (const config of previewAlertBannerConfigs) {
    const result = config.isRendered(args)
    if (result === NO_MATCH) continue
    return config.build(args, result)
  }
  return null
}
